import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ExceptionPreview } from '@/api/planned';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

/** The panel's title id: the host dialog's accessible name while it shows. */
export const EXCEPTIONS_TITLE_ID = 'exceptions-title';

interface ExceptionsPanelProps {
  rows: ExceptionPreview[];
  onClose: () => void;
  onChoose: (policy: 'KEEP' | 'RESET') => void;
  /** The series save is in flight: Save shows its loading state and the dialog cannot be dismissed. */
  busy?: boolean;
}

/**
 * Keep / Reset pending-exceptions preview (TMMAPP-0009, MM-D-58): the content of a dialog — title,
 * the exceptions, the policy, Cancel and Save. The series editor shows it in place of its form, in
 * its own dialog (TMMAPP-0082, DLG-005); `busy` (the series save in flight) disables Cancel and the
 * host ignores `Esc`.
 */
export function ExceptionsPanel({ rows, onClose, onChoose, busy = false }: ExceptionsPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [policy, setPolicy] = useState<'KEEP' | 'RESET'>('KEEP');
  // The panel replaces the series form inside a dialog that stays mounted, so focus would be left on
  // the Save that has just unmounted: it moves to the policy choice, where the step starts.
  const keepRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);

  return (
    <>
      <DialogTitle id={EXCEPTIONS_TITLE_ID}>{t('planned.exceptionsTitle')}</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('planned.originalDate')}</TableCell>
              <TableCell>{t('planned.occursOn')}</TableCell>
              <ActionsHeaderCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.originalOn}>
                <TableCell>{formatCivilDate(row.originalOn, locale)}</TableCell>
                <TableCell>{formatCivilDate(row.occursOn, locale)}</TableCell>
                <TableCell>
                  <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {row.differs.date && <Chip size="small" label={t('planned.occursOn')} />}
                    {row.differs.amount && <Chip size="small" label={t('planned.amount')} />}
                    {row.differs.payee && <Chip size="small" label={t('planned.payee')} />}
                    {row.differs.slices && <Chip size="small" label={t('planned.targets')} />}
                    {row.wouldOrphanUnder === true && (
                      <Typography
                        variant="caption"
                        sx={{ color: (theme) => resolveTokens(theme).errorText }}
                      >
                        {t('planned.willBeDropped')}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <FormControl sx={{ mt: 2 }}>
          <RadioGroup
            value={policy}
            onChange={(_event, value) => setPolicy(value as 'KEEP' | 'RESET')}
          >
            <FormControlLabel
              value="KEEP"
              control={<Radio slotProps={{ input: { ref: keepRef } }} />}
              label={t('planned.keepExceptions')}
            />
            <FormHelperText>{t('planned.keepExceptionsHelp')}</FormHelperText>
            <FormControlLabel
              value="RESET"
              control={<Radio />}
              label={t('planned.resetExceptions')}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <SubmitButton loading={busy} onClick={() => onChoose(policy)}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
